import { Link } from '@tanstack/react-router';
import type React from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '~/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '~/components/ui/table';
import { cn } from '~/lib/utils';
import { formatDate } from '~/utils/date';

export interface QuestionScoreRow {
  id: string;
  questionId: string;
  category: string;
  questionText: string;
  verdict: string | null;
  score: number | null;
  reasoning: string;
  evidence: string;
  competencyName: string | null;
}

export interface ApplicationResult {
  applicationId: string;
  stageId: string;
  candidateName: string;
  candidateEmail: string;
  scoreId: string | null;
  scoreStatus: string | null;
  overallScore: number | null;
  recommendation: string | null;
  eligibilityPassed: string | null;
  expectationsPassed: string | null;
  assessmentAverage: string | null;
  scoredAt: string | null;
  questionScores: QuestionScoreRow[];
}

export interface Stage {
  id: string;
  name: string;
}

export interface Question {
  id: string;
  category: string;
  text: string;
}

interface ResultsTableProps {
  results: ApplicationResult[];
  questions: Question[];
  orgSlug: string;
  deptSlug: string;
  jobId: string;
}

const CATEGORY_ORDER = ['eligibility', 'job_expectations', 'assessment', 'information'];

const CATEGORY_LABEL_CLASS: Record<string, string> = {
  eligibility: 'text-[var(--color-section-blue)]',
  job_expectations: 'text-[var(--color-section-green)]',
  assessment: 'text-[var(--color-section-orange)]',
  information: 'text-[var(--color-section-purple)]'
};

function getRecommendationVariant(rec: string): 'secondary' | 'destructive' | 'outline' {
  if (rec === 'shortlist') return 'secondary';
  if (rec === 'reject') return 'destructive';
  return 'secondary';
}

function getStatusVariant(status: string | null): 'secondary' | 'destructive' | 'outline' {
  if (status === 'completed') return 'secondary';
  if (status === 'failed') return 'destructive';
  if (status === 'pending' || status === 'processing') return 'secondary';
  return 'outline';
}

export function generateCsv(
  results: ApplicationResult[],
  stages: Stage[],
  questions: Question[],
  jobTitle: string
): string {
  const stageMap = new Map(stages.map((s) => [s.id, s.name]));
  const scoredQuestions = questions.filter((q) => q.category !== 'information');
  const fixedHeaders = [
    'Candidate Name',
    'Email',
    'Role',
    'Stage',
    'Overall Score',
    'Recommendation',
    'Eligibility',
    'Expectations',
    'Avg Score',
    'Interview Date',
    'Status'
  ];
  const headers = [...fixedHeaders, ...scoredQuestions.map((q) => q.text)];
  const rows = results.map((r) => {
    const scoreMap = new Map(r.questionScores.map((qs) => [qs.questionId, qs]));
    const fixedCols = [
      r.candidateName,
      r.candidateEmail,
      jobTitle,
      stageMap.get(r.stageId) ?? r.stageId,
      r.overallScore?.toString() ?? '',
      r.recommendation ?? '',
      r.eligibilityPassed ?? '',
      r.expectationsPassed ?? '',
      r.assessmentAverage ?? '',
      r.scoredAt ? formatDate(r.scoredAt) : '',
      r.scoreStatus ?? 'no_interview'
    ];
    const questionCols = scoredQuestions.map((q) => {
      const qs = scoreMap.get(q.id);
      if (!qs) return '';
      return qs.reasoning;
    });
    return [...fixedCols, ...questionCols];
  });
  const escape = (val: string): string =>
    val.includes(',') || val.includes('"') || val.includes('\n')
      ? `"${val.replace(/"/g, '""')}"`
      : val;
  return [headers, ...rows].map((row) => row.map(escape).join(',')).join('\n');
}

export function ResultsTable({
  results,
  questions,
  orgSlug,
  deptSlug,
  jobId
}: ResultsTableProps): React.ReactNode {
  const { t } = useTranslation();

  const sortedQuestions = [...questions].sort(
    (a, b) => CATEGORY_ORDER.indexOf(a.category) - CATEGORY_ORDER.indexOf(b.category)
  );

  if (results.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-20">
        <p className="text-base font-semibold text-foreground">{t('results.emptyTitle')}</p>
        <p className="max-w-sm text-center text-sm text-muted-foreground">
          {t('results.emptyDescription')}
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border">
      <Table className="min-w-max">
        <TableHeader>
          <TableRow>
            <TableHead className="sticky left-0 z-10 w-52 bg-background px-4 py-3">
              {t('results.columns.candidate')}
            </TableHead>
            {sortedQuestions.map((q) => (
              <TableHead key={q.id} className="w-56 align-top whitespace-normal px-4 py-3">
                <p
                  className={cn(
                    'mb-1 text-xs font-semibold uppercase tracking-wide',
                    CATEGORY_LABEL_CLASS[q.category]
                  )}
                >
                  {t(
                    `results.questionSections.${q.category}` as 'results.questionSections.eligibility'
                  )}
                </p>
                <p className="text-sm font-medium text-foreground">{q.text}</p>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {results.map((r) => {
            const scoreMap = new Map(r.questionScores.map((qs) => [qs.questionId, qs]));
            return (
              <TableRow key={r.applicationId}>
                <TableCell className="sticky left-0 z-10 w-52 bg-background align-top px-4 py-3">
                  <div className="flex flex-col gap-2">
                    <div>
                      <Link
                        to="/org/$orgSlug/department/$deptSlug/jobs/$jobId/$applicationId"
                        params={{ orgSlug, deptSlug, jobId, applicationId: r.applicationId }}
                        className="text-sm font-semibold hover:underline"
                      >
                        {r.candidateName}
                      </Link>
                      <p className="text-xs text-muted-foreground">{r.candidateEmail}</p>
                    </div>
                    {r.overallScore !== null && (
                      <p className="text-xl font-bold leading-none">
                        {r.overallScore}
                        <span className="ml-0.5 text-xs font-normal text-muted-foreground">
                          / 10
                        </span>
                      </p>
                    )}
                    <div className="flex flex-wrap gap-1">
                      {r.recommendation && (
                        <Badge
                          variant={getRecommendationVariant(r.recommendation)}
                          className={cn(
                            r.recommendation === 'shortlist' &&
                              'border-blue-200 bg-blue-100 text-blue-700 hover:bg-blue-100'
                          )}
                        >
                          {t(
                            `scoring.recommendations.${r.recommendation}` as 'scoring.recommendations.shortlist'
                          )}
                        </Badge>
                      )}
                      <Badge
                        variant={getStatusVariant(r.scoreStatus)}
                        className={cn(!r.scoreStatus && 'text-muted-foreground')}
                      >
                        {r.scoreStatus
                          ? t(
                              `results.statusLabels.${r.scoreStatus}` as 'results.statusLabels.pending'
                            )
                          : t('results.statusLabels.no_interview')}
                      </Badge>
                    </div>
                  </div>
                </TableCell>
                {sortedQuestions.map((q) => {
                  const qs = scoreMap.get(q.id);
                  return (
                    <TableCell key={q.id} className="w-56 align-top whitespace-normal px-4 py-3">
                      {qs ? (
                        <div className="flex flex-col gap-1.5">
                          {q.category === 'assessment' && qs.score !== null && (
                            <p className="text-base font-bold leading-none">
                              {qs.score}
                              <span className="ml-0.5 text-xs font-normal text-muted-foreground">
                                / 5
                              </span>
                            </p>
                          )}
                          {q.category !== 'assessment' &&
                            q.category !== 'information' &&
                            qs.verdict && (
                              <Badge
                                variant={qs.verdict === 'fail' ? 'destructive' : 'secondary'}
                                className={cn(
                                  'w-fit',
                                  qs.verdict === 'pass' &&
                                    'border-green-200 bg-green-100 text-green-700 hover:bg-green-100'
                                )}
                              >
                                {qs.verdict}
                              </Badge>
                            )}
                          {qs.reasoning && (
                            <p className="text-sm text-muted-foreground">{qs.reasoning}</p>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground/50">—</span>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
